import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../lib/api';
import { streamChat, getOrCreateSession } from '../lib/aiStream';
import { renderChatText } from '../lib/chatText';
import { Sparkles, Send } from 'lucide-react';

const SUGGESTIONS = [
  'Which combinations does the guide list for recovery research?',
  'What does the guide say about GHK-Cu and skin research?',
  'How do I verify a peptide supplier is legit?',
  'What should I know about storing peptides?',
];

export const LibraryConcierge = () => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => getOrCreateSession('alp_library_ai_session'));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/ai/library/history/${sessionId}`).then((r) => r.json()).then(setMessages).catch(() => {});
  }, [sessionId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', text: msg }, { role: 'assistant', text: '' }]);
    try {
      await streamChat(`${API}/ai/library`, {
        sessionId, message: msg,
        onDelta: (d) => setMessages((m) => {
          const n = [...m];
          n[n.length - 1] = { ...n[n.length - 1], text: n[n.length - 1].text + d };
          return n;
        }),
      });
    } catch (e) {
      setMessages((m) => {
        const n = [...m];
        n[n.length - 1] = { role: 'assistant', text: 'Sorry, I had trouble answering just now. Please try again.' };
        return n;
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="alp-beam rounded-xl p-[1.5px]" data-testid="library-concierge">
      <div className="rounded-[calc(0.75rem-1.5px)] bg-alp-black/95 p-6">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={18} className="text-alp-gold" />
          <h3 className="font-serif text-2xl text-alp-cream">Ask the Guides</h3>
        </div>
        <p className="text-alp-gray text-xs mb-5">Your concierge has read every client guide — the 650 combinations, the 155 protocols and the supplier checklist. Ask anything. Educational, research-only reference.</p>

        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2 mb-5" data-testid="library-concierge-suggestions">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} type="button" onClick={() => send(s)} data-testid={`library-concierge-suggestion-${i}`}
                className="text-left text-xs text-alp-cream/80 border border-alp-line rounded-full px-4 py-2 hover:border-alp-gold/60 hover:text-alp-gold transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.length > 0 && (
          <div className="max-h-96 overflow-y-auto space-y-3 mb-5 pr-1" data-testid="library-concierge-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-alp-gold text-alp-black' : 'bg-alp-card/70 border border-alp-line text-alp-cream/90'}`}>
                  {m.text ? (m.role === 'assistant' ? renderChatText(m.text, (path) => navigate(path)) : m.text) : (busy && i === messages.length - 1 ? '\u2026' : '')}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
        )}

        <div className="flex gap-2">
          <input
            value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Ask the guides anything…" data-testid="library-concierge-input"
            className="flex-1 bg-alp-dark/60 border border-alp-line rounded-full px-5 py-3 text-sm text-alp-cream placeholder-alp-gray focus:outline-none focus:border-alp-gold transition-colors"
          />
          <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="library-concierge-send"
            className="shrink-0 w-11 h-11 rounded-full bg-alp-gold text-alp-black flex items-center justify-center hover:bg-alp-goldlight transition-colors disabled:opacity-50">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
